import axios from 'axios';
import { CATEGORIES } from '../types/api';
import { prepareImage } from '@/lib/image';
import type {
  AppSummary,
  VersionInfo,
  AppManifest,
  DeveloperProfile,
  Attestation,
  Org,
  OrgMetadata,
  OrgMember,
  OrgInvitation,
  MyOrgInvitation,
  OrgPackageList,
  ApiToken,
} from '@/types/api';

const AUTH_SESSION_FLAG = 'app_registry_authenticated';

export const api = axios.create({
  baseURL:
    (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ||
    '/api',
  timeout: 10000,
  withCredentials: true, // send cookies (session) with requests
});

// GET /api/v2/bundles is cached at the edge and nothing can purge it (Vercel
// offers no purge API outside Next.js), so a listing can lag the registry by
// the length of that cache window — see api/v2/bundles/index.js. Fine for
// browsing, wrong for whoever just changed something: delete a package, get
// dropped on /apps, and seeing it still listed reads as a delete that failed.
//
// So a successful mutation opens a short window during which this tab asks for
// uncached listings instead. The window only has to outlast the edge cache; it
// lives in sessionStorage rather than a module variable so it survives a full
// reload, which is exactly what someone double-checks a suspicious delete with.
const LISTING_FRESH_UNTIL = 'app_registry_listing_fresh_until';
const LISTING_FRESH_WINDOW_MS = 90_000;

/** The one cached endpoint. Its siblings set no cache headers. */
function isCachedListingRead(config: {
  method?: string;
  url?: string;
}): boolean {
  if (String(config.method || 'get').toLowerCase() !== 'get') return false;
  const path = String(config.url || '')
    .split('?')[0]
    .replace(/\/+$/, '');
  return path === '/v2/bundles' || path === 'v2/bundles';
}

/** Anything that changes what a listing should say: push, yank, delete, verify. */
function isListingMutation(config?: {
  method?: string;
  url?: string;
}): boolean {
  const method = String(config?.method || 'get').toLowerCase();
  if (method === 'get' || method === 'head' || method === 'options')
    return false;
  const path = String(config?.url || '');
  return (
    /^\/?v2\/bundles(\/|$)/.test(path) || /^\/?admin\/packages(\/|$)/.test(path)
  );
}

function listingFreshnessActive(): boolean {
  if (typeof window === 'undefined') return false;
  const until = Number(
    window.sessionStorage.getItem(LISTING_FRESH_UNTIL) || '0'
  );
  if (!until) return false;
  if (Date.now() < until) return true;
  window.sessionStorage.removeItem(LISTING_FRESH_UNTIL);
  return false;
}

function openListingFreshnessWindow(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(
    LISTING_FRESH_UNTIL,
    String(Date.now() + LISTING_FRESH_WINDOW_MS)
  );
}

api.interceptors.request.use(config => {
  if (isCachedListingRead(config) && listingFreshnessActive()) {
    config.params = { ...(config.params || {}), fresh: '1' };
  }
  return config;
});

// Its own instance, sharing the configured baseURL so the refresh reaches the
// same host as everything else (the frontend deploys separately from the API),
// but carrying no interceptor, so a 401 on refresh cannot recurse.
const authClient = axios.create({
  baseURL: api.defaults.baseURL,
  timeout: 10000,
  withCredentials: true,
});

// Shared across concurrent 401s so a page issuing several requests refreshes
// once rather than spending one single-use refresh token per request.
// `expired` and `anonymous` are both a refused refresh, told apart by which
// error the server sent: only the first means there was a session to lose, and
// only it should send the user to /login.
type RefreshOutcome = 'renewed' | 'expired' | 'anonymous' | 'unavailable';

let refreshInFlight: Promise<RefreshOutcome> | null = null;

function refreshSession(): Promise<RefreshOutcome> {
  refreshInFlight ??= authClient
    .post('/auth/refresh', null)
    .then<RefreshOutcome>(() => {
      // This tab is authenticated now even if it never ran a login itself,
      // which is what a link opened in a new tab looks like.
      window.sessionStorage.setItem(AUTH_SESSION_FLAG, '1');
      return 'renewed';
    })
    .catch<RefreshOutcome>(err => {
      // Only the server saying the credential is no good ends the session. A
      // 5xx or a dropped connection says nothing about it, and signing the
      // user out over a transient fault loses their work for no reason.
      const status = err?.response?.status;
      if (status !== 401 && status !== 403) return 'unavailable';
      return err?.response?.data?.error === 'no_refresh_token'
        ? 'anonymous'
        : 'expired';
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

// Error interceptor
api.interceptors.response.use(
  response => {
    if (isListingMutation(response.config)) openListingFreshnessWindow();
    return response;
  },
  async error => {
    const status = error.response?.status;
    const apiError = error.response?.data;
    const requestUrl = String(error.config?.url || '');
    const originalMessage = String(error.cause?.message || error.message || '');

    if (typeof window !== 'undefined' && status === 401) {
      // Attempted whatever AUTH_SESSION_FLAG says. The flag lives in
      // sessionStorage, which is per-tab, so a link opened in a new tab has no
      // flag while still carrying a perfectly good refresh cookie. Gating the
      // recovery on it would leave that tab unable to help itself. A signed-out
      // visitor just gets one cheap 401 from the refresh endpoint.
      // `_retried` bounds this to a single attempt so a persistent 401 cannot loop.
      const original = error.config;
      const isRefreshCall = requestUrl.includes('/auth/refresh');
      if (original && !original._retried && !isRefreshCall) {
        original._retried = true;
        const outcome = await refreshSession();

        if (outcome === 'renewed') return api(original);

        if (outcome === 'expired') {
          // Refused, but a refusal proves this tab was holding a credential,
          // so record that the tab had a session. Then retry anyway: a tab
          // that lost a rotation race sees its own token refused while the
          // winning tab has already put fresh cookies in the shared jar. If
          // the retry 401s too, `_retried` sends it to the sign-out below,
          // where the flag now guarantees the redirect happens.
          window.sessionStorage.setItem(AUTH_SESSION_FLAG, '1');
          return api(original);
        }

        // Unreachable: say nothing about the session, just fail the request.
        if (outcome === 'unavailable') return Promise.reject(error);

        // 'anonymous' - nothing was presented. Falls through rather than
        // returning, so a tab that believed it was signed in still gets signed
        // out; a visitor who never was simply fails the flag check below.
      }
    }

    // Only a tab that believed it was signed in. An anonymous visitor's 401 is
    // not an expiry.
    if (
      typeof window !== 'undefined' &&
      status === 401 &&
      window.sessionStorage.getItem(AUTH_SESSION_FLAG) === '1'
    ) {
      window.sessionStorage.removeItem(AUTH_SESSION_FLAG);
      if (window.location.pathname !== '/login') {
        const from = `${window.location.pathname}${window.location.search}`;
        window.location.assign(
          `/login?error=session_expired&from=${encodeURIComponent(from)}`
        );
      }
    }

    // Friendly message when the API cannot be reached at all
    if (error.code === 'ERR_NETWORK' || !error.response) {
      if (requestUrl.includes('/v2/bundles/push-file')) {
        error.message = originalMessage.includes('ERR_UPLOAD_FILE_CHANGED')
          ? 'The selected .mpk file changed on disk. Re-select the rebuilt file and try again.'
          : 'Upload failed before the server responded. If you rebuilt the .mpk, re-select it and try again.';
      } else {
        error.message =
          'Backend is not running. Start both backend and frontend: pnpm dev:all';
      }
    }
    // A 401 from the /auth/me session check is how an anonymous visitor is
    // told "not signed in" — an expected answer, not an error to log.
    const isSignedOutCheck =
      status === 401 && /\/auth\/me(\?|$)/.test(requestUrl);
    if (!isSignedOutCheck) {
      // eslint-disable-next-line no-console
      console.error('API Error:', apiError || error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * One bundle -> one AppSummary. Extracted because getApps and getMyPackages
 * each carried their own copy, so a field added to one silently missed the
 * other.
 *
 * Every field the cards render is optional in production data: three live
 * bundles have no icon, and `installSize` / `publishedAt` are null for
 * everything published before the metadata policy shipped. Pass the absence
 * through as undefined/null rather than defaulting — a `0` size and a
 * `new Date(null)` both render as confident nonsense.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toAppSummary = (bundle: any): AppSummary => {
  const author = bundle.metadata?.author || 'Unknown';
  // ⚠️ TWO CLAIMS, CARRIED SEPARATELY. `verified` is the admin's decision
  // about the package; `publisherVerified` is about the person. Mapping one
  // onto both — which this did — is how a single value came to make two
  // different assertions on the same card.
  const verified = !!bundle.verified;
  const publisherVerified = !!bundle.publisherVerified;
  const tags: string[] = Array.isArray(bundle.metadata?.tags)
    ? bundle.metadata.tags
    : [];

  // Resolved category. `metadata.category` only exists once a cargo-mero
  // release carries the field; until then publishers declare it as a tag, so
  // reading the explicit field alone finds nothing.
  const declared =
    typeof bundle.metadata?.category === 'string'
      ? bundle.metadata.category.trim().toLowerCase()
      : '';
  const fromTag = tags
    .map(t => (typeof t === 'string' ? t.trim().toLowerCase() : ''))
    .map(t => (t === 'game' ? 'games' : t))
    .find(t => (CATEGORIES as readonly string[]).includes(t));
  const resolved = (CATEGORIES as readonly string[]).includes(declared)
    ? declared
    : fromTag;

  return {
    id: bundle.package,
    name: bundle.metadata?.name || bundle.package,
    package_name: bundle.package,
    developer_pubkey: author,
    latest_version: bundle.appVersion,
    alias: bundle.metadata?.name,
    downloads: bundle.downloads || 0,
    verified,
    publisherVerified,
    icon: bundle.metadata?.icon || undefined,
    description: bundle.metadata?.description || undefined,
    tags,
    category: resolved as AppSummary['category'],
    installSize:
      typeof bundle.installSize === 'number' ? bundle.installSize : null,
    publishedAt:
      typeof bundle.publishedAt === 'string' ? bundle.publishedAt : null,
    developer: {
      display_name: author,
      pubkey: author,
      // The developer's own record: the publisher's claim, not the package's.
      verified: publisherVerified,
    },
  };
};

export const getApps = async (params?: {
  dev?: string;
  name?: string;
}): Promise<AppSummary[]> => {
  // Use V2 Bundle API
  const v2Params: Record<string, string> = {};
  if (params?.dev) {
    v2Params.developer = params.dev;
  }
  if (params?.name) {
    v2Params.package = params.name;
  }

  const response = await api.get('/v2/bundles', { params: v2Params });
  const bundles = Array.isArray(response.data) ? response.data : [];

  return bundles.map(toAppSummary);
};

/** Fetch the signed-in account's packages, plus any whose metadata.author is its username. */
export const getMyPackages = async (params: {
  username?: string | null;
}): Promise<AppSummary[]> => {
  const username = params.username?.trim();
  const queries: Record<string, string>[] = [{ mine: '1' }];
  if (username) queries.push({ author: username });

  const responses = await Promise.all(
    queries.map(query => api.get('/v2/bundles', { params: query }))
  );

  const bundles = responses.flatMap(response =>
    Array.isArray(response.data) ? response.data : []
  );

  const uniqueBundles = Array.from(
    new Map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      bundles.map((bundle: any) => [
        `${bundle.package}@${bundle.appVersion}`,
        bundle,
      ])
    ).values()
  );

  return uniqueBundles.map(toAppSummary);
};

export const getAppVersions = async (appId: string): Promise<VersionInfo[]> => {
  // Use V2 Bundle API - get all bundles for this package (including yanked)
  const response = await api.get('/v2/bundles', {
    params: { package: appId, all_versions: 'true' },
  });
  const bundles = Array.isArray(response.data) ? response.data : [];

  // Transform V2 bundles to VersionInfo format
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return bundles.map((bundle: any) => {
    // Get artifact URL (convention: /artifacts/:package/:version/:package-:version.mpk)
    const artifactUrl = `/artifacts/${bundle.package}/${bundle.appVersion}/${bundle.package}-${bundle.appVersion}.mpk`;
    return {
      semver: bundle.appVersion,
      cid: artifactUrl,
      yanked: bundle.yanked === true,
    };
  });
};

export const getAppManifest = async (
  appId: string,
  semver: string
): Promise<AppManifest> => {
  // Use V2 Bundle API
  const response = await api.get(`/v2/bundles/${appId}/${semver}`);
  const bundle = response.data;

  // Transform V2 BundleManifest to frontend AppManifest format
  const artifactUrl = `/artifacts/${bundle.package}/${bundle.appVersion}/${bundle.package}-${bundle.appVersion}.mpk`;

  return {
    manifest_version: bundle.version,
    min_runtime_version: bundle.min_runtime_version ?? '0.1.0',
    app: {
      name: bundle.metadata?.name || bundle.package,
      developer_pubkey: bundle.signature?.pubkey || 'unknown',
      id: bundle.package,
      alias: bundle.metadata?.name,
    },
    version: {
      semver: bundle.appVersion,
    },
    supported_chains: [], // V2 bundles don't have chains in manifest
    permissions: [
      {
        cap: 'basic',
        bytes: bundle.wasm?.size || 0,
      },
    ],
    artifacts: [
      {
        type: 'mpk',
        target: 'node',
        cid: artifactUrl,
        size: bundle.wasm?.size || 0,
      },
    ],
    metadata: {
      provides: bundle.interfaces?.exports || [],
      requires: bundle.interfaces?.uses || [],
      description: bundle.metadata?.description,
      tags: bundle.metadata?.tags,
      license: bundle.metadata?.license,
      links: bundle.links,
    },
    distribution: 'registry',
    signature: bundle.signature
      ? {
          alg: bundle.signature.alg,
          sig: bundle.signature.sig,
          signed_at: bundle.signature.signedAt,
        }
      : {
          alg: 'ed25519',
          sig: 'unsigned',
          signed_at: new Date().toISOString(),
        },
  };
};

export const getDeveloper = async (
  pubkey: string
): Promise<DeveloperProfile> => {
  const response = await api.get(`/developers/${pubkey}`);
  return response.data;
};

export const getAttestation = async (
  pubkey: string,
  appName: string,
  semver: string
): Promise<Attestation> => {
  const response = await api.get(
    `/attestations/${pubkey}/${appName}/${semver}`
  );
  return response.data;
};

/** Get raw V2 bundle manifest for a single version (for edit page). */
export const getBundleManifestRaw = async (
  packageName: string,
  version: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<Record<string, any>> => {
  const response = await api.get(`/v2/bundles/${packageName}/${version}`);
  return response.data;
};

/** Yank (or unyank) a specific version of a package. Requires login as the package author. */
export const yankBundleVersion = async (
  packageName: string,
  version: string,
  yanked: boolean
): Promise<void> => {
  await api.post(
    `/v2/bundles/${encodeURIComponent(packageName)}/${encodeURIComponent(version)}/yank`,
    { yanked }
  );
};

/** Delete a specific version of a package. Requires login as the package author. */
export const deleteBundleVersion = async (
  packageName: string,
  version: string
): Promise<void> => {
  await api.delete(
    `/v2/bundles/${encodeURIComponent(packageName)}/${encodeURIComponent(version)}`
  );
};

/** Delete an entire package (all versions). Requires login as the package author. */
export const deletePackage = async (packageName: string): Promise<void> => {
  await api.delete(`/v2/bundles/${encodeURIComponent(packageName)}`);
};

/** Push a .mpk bundle file to the registry (multipart). Returns { package, version } on success. */
export const pushBundleFile = async (
  file: File
): Promise<{ package: string; version: string }> => {
  const form = new FormData();
  form.append('bundle', file);
  const response = await api.post('/v2/bundles/push-file', form, {
    timeout: 120000,
  });
  return {
    package: response.data.package,
    version: response.data.version,
  };
};

// ——— Organizations (V2 org API) ———

/** List orgs the given member (email) belongs to. */
export const getOrgsByMember = async (memberEmail: string): Promise<Org[]> => {
  if (!memberEmail?.trim()) return [];
  const response = await api.get<Org[]>('/v2/orgs', {
    params: { member: memberEmail.trim() },
  });
  return Array.isArray(response.data) ? response.data : [];
};

/** Get the org that owns a package (reverse lookup). Returns null if unlinked. */
export const getOrgByPackage = async (
  packageName: string
): Promise<Org | null> => {
  if (!packageName?.trim()) return null;
  const response = await api.get<Org | null>('/v2/orgs', {
    params: { package: packageName.trim() },
  });
  return response.data ?? null;
};

/** Get a single org by id. */
export const getOrg = async (orgId: string): Promise<Org | null> => {
  const response = await api.get<Org>(`/v2/orgs/${encodeURIComponent(orgId)}`);
  return response.data ?? null;
};

/** List members of an org. */
export const getOrgMembers = async (
  orgId: string
): Promise<{ members: OrgMember[] }> => {
  const response = await api.get<{ members: OrgMember[] }>(
    `/v2/orgs/${encodeURIComponent(orgId)}/members`
  );
  return {
    members: Array.isArray(response.data?.members) ? response.data.members : [],
  };
};

/** List packages linked to an org. */
export const getOrgPackages = async (
  orgId: string
): Promise<OrgPackageList> => {
  const response = await api.get<OrgPackageList>(
    `/v2/orgs/${encodeURIComponent(orgId)}/packages`
  );
  return {
    packages: Array.isArray(response.data?.packages)
      ? response.data.packages
      : [],
  };
};

// ——— Org write operations (session cookie auth — sent automatically via withCredentials) ———

/** Create org. Requires Google login. Creator becomes first admin. */
export const createOrg = async (name: string, slug: string): Promise<Org> => {
  const response = await api.post<Org>('/v2/orgs', {
    name: name.trim(),
    slug: slug.toLowerCase().trim(),
  });
  return response.data;
};

/** Update org (admin only). */
export const updateOrg = async (
  orgId: string,
  updates: { name?: string; metadata?: OrgMetadata }
): Promise<Org> => {
  const response = await api.patch<Org>(
    `/v2/orgs/${encodeURIComponent(orgId)}`,
    updates
  );
  return response.data;
};

/** Invite a user to an org by username (admin only). They join once they accept. */
export const addOrgMember = async (
  orgId: string,
  username: string,
  role: 'admin' | 'member' = 'member'
): Promise<{ invited: boolean }> => {
  const response = await api.post(
    `/v2/orgs/${encodeURIComponent(orgId)}/members`,
    {
      username: username.trim().replace(/^@+/, ''),
      role,
    }
  );
  return { invited: response.status === 202 };
};

/** Remove member from org by username or email (admin only, or self). */
export const removeOrgMember = async (
  orgId: string,
  member: string
): Promise<void> => {
  await api.delete(
    `/v2/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(member)}`
  );
};

/** Pending invitations of an org (admin only). */
export const getOrgInvitations = async (
  orgId: string
): Promise<OrgInvitation[]> => {
  const response = await api.get<{ invitations: OrgInvitation[] }>(
    `/v2/orgs/${encodeURIComponent(orgId)}/invitations`
  );
  return Array.isArray(response.data?.invitations)
    ? response.data.invitations
    : [];
};

/** Revoke a pending invitation (admin only). */
export const revokeOrgInvitation = async (
  orgId: string,
  username: string
): Promise<void> => {
  await api.delete(
    `/v2/orgs/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(username)}`
  );
};

/** The signed-in user's pending org invitations. */
export const getMyOrgInvitations = async (): Promise<MyOrgInvitation[]> => {
  const response = await api.get<{ invitations: MyOrgInvitation[] }>(
    '/v2/invitations'
  );
  return Array.isArray(response.data?.invitations)
    ? response.data.invitations
    : [];
};

/** Accept or decline an org invitation. */
export const respondToOrgInvitation = async (
  orgId: string,
  action: 'accept' | 'decline'
): Promise<void> => {
  await api.post(`/v2/invitations/${encodeURIComponent(orgId)}/${action}`);
};

/** Link package to org (admin only; must be package author). */
export const linkOrgPackage = async (
  orgId: string,
  packageName: string
): Promise<void> => {
  await api.post(`/v2/orgs/${encodeURIComponent(orgId)}/packages`, {
    package: packageName.trim(),
  });
};

/** Unlink package from org (admin only). */
export const unlinkOrgPackage = async (
  orgId: string,
  packageName: string
): Promise<void> => {
  await api.delete(
    `/v2/orgs/${encodeURIComponent(orgId)}/packages/${encodeURIComponent(packageName)}`
  );
};

/** Delete an org and all its data (admin only). */
export const deleteOrg = async (orgId: string): Promise<void> => {
  await api.delete(`/v2/orgs/${encodeURIComponent(orgId)}`);
};

// ——— API Tokens ———

/** Create a new API token for CLI use. Returns full token (shown only once). */
export const createApiToken = async (
  label?: string
): Promise<{ token: string; label: string; createdAt: string }> => {
  const response = await api.post<{
    token: string;
    label: string;
    createdAt: string;
  }>('/auth/token', { label: label || 'CLI token' });
  return response.data;
};

/** List existing API tokens (masked). */
export const listApiTokens = async (): Promise<ApiToken[]> => {
  const response = await api.get<{ tokens: ApiToken[] }>('/auth/tokens');
  return Array.isArray(response.data?.tokens) ? response.data.tokens : [];
};

/** Revoke an API token by its tokenId (first 8 chars). */
export const revokeApiToken = async (tokenId: string): Promise<void> => {
  await api.delete(`/auth/token/${encodeURIComponent(tokenId)}`);
};

/** Claim a username for the currently logged-in user. Can only be done once. */
export const claimUsername = async (
  username: string
): Promise<{ username: string }> => {
  const response = await api.post<{ username: string }>('/auth/username', {
    username,
  });
  return response.data;
};

/** Batch-resolve emails to { username, verified }. Returns a map keyed by email. */
export const resolveUsers = async (
  emails: string[]
): Promise<Record<string, { username: string | null; verified: boolean }>> => {
  if (!emails.length) return {};
  const response = await api.get<
    Record<string, { username: string | null; verified: boolean }>
  >('/users/resolve', {
    params: { emails: emails.join(',') },
  });
  return response.data ?? {};
};

// ——— Package assets (screenshots / video) ———
//
// Assets are registry-side state, not part of the signed bundle, so they are
// fetched per package rather than read off the manifest. The listing is
// moderation-gated server-side: an unapproved package returns an empty list
// to everyone except its owner and site admins, so `assets: []` is a normal
// answer and not an error.

export interface PackageAsset {
  id: string;
  kind: 'image' | 'video';
  contentType: string;
  bytes: number;
  alt: string;
  order: number;
  /** Always an API path, never a bucket URL — reads are gated on each request. */
  url: string;
  /**
   * What a TILE should load: a downscaled copy when one exists, otherwise the
   * same path as `url`. Use `url` only when showing the picture full screen —
   * loading it in the strip is what made an app page with eight screenshots
   * fetch up to 32MB to fill eight 176px boxes.
   */
  thumbUrl?: string;
  hasThumb?: boolean;
  thumbBytes?: number | null;
  /** Intrinsic size of the original, when the uploader measured it. */
  width?: number | null;
  height?: number | null;
}

export interface PackageAssets {
  assets: PackageAsset[];
  state: 'approved' | 'pending';
  /** True when the caller can see assets that the public cannot yet. */
  pendingApproval?: boolean;
}

export const getPackageAssets = async (pkg: string): Promise<PackageAssets> => {
  const { data } = await api.get(
    `/v2/packages/${encodeURIComponent(pkg)}/assets`
  );
  return {
    assets: Array.isArray(data?.assets) ? data.assets : [],
    state: data?.state === 'approved' ? 'approved' : 'pending',
    pendingApproval: !!data?.pendingApproval,
  };
};

/**
 * Upload one file. Base64 because the serverless routes take JSON, not
 * multipart.
 *
 * A downscaled copy rides along when the browser could make one, so the strip
 * has something small to load. `prepareImage` never throws and returns nulls
 * on every failure path, so an upload is never blocked by it — see lib/image.
 */
export const uploadPackageAsset = async (
  pkg: string,
  file: File,
  alt = ''
): Promise<PackageAsset> => {
  const [data, prepared] = await Promise.all([
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Could not read the file.'));
      // readAsDataURL gives "data:<type>;base64,<payload>" — the server sniffs
      // the bytes itself, so only the payload is sent.
      reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
      reader.readAsDataURL(file);
    }),
    prepareImage(file),
  ]);
  const res = await api.post(`/v2/packages/${encodeURIComponent(pkg)}/assets`, {
    data,
    alt,
    thumb: prepared.thumb ?? undefined,
    width: prepared.width ?? undefined,
    height: prepared.height ?? undefined,
  });
  return res.data.asset;
};

export const deletePackageAsset = async (pkg: string, id: string) => {
  await api.delete(`/v2/packages/${encodeURIComponent(pkg)}/assets/${id}`);
};

export const reorderPackageAssets = async (
  pkg: string,
  assets: { id: string; alt?: string }[]
): Promise<PackageAsset[]> => {
  const res = await api.patch(
    `/v2/packages/${encodeURIComponent(pkg)}/assets`,
    { assets }
  );
  return res.data.assets;
};

// ——— Moderation review queue (admin) ———
//
// Packages whose newest upload postdates their last decision. ⚠️ Keyed on the
// ASSETS, not on the state: a package approved last month that uploaded a
// screenshot this morning is waiting again, or the first approval becomes a
// licence to publish anything afterwards.

export interface ReviewAsset {
  id: string;
  kind: 'image' | 'video';
  contentType: string;
  bytes: number;
  alt: string;
  order: number;
  uploadedAt: string | null;
  /** Full size. For the lightbox, not for a tile. */
  url: string;
  thumbUrl?: string;
  width?: number | null;
  height?: number | null;
}

export interface ReviewItem {
  package: string;
  latestVersion: string | null;
  metadata: Record<string, unknown> & { name?: string; description?: string };
  author: string;
  state: 'pending' | 'approved' | 'declined';
  decidedAt: string | null;
  decidedBy: string | null;
  reason: string;
  newestAssetAt: string | null;
  assets: ReviewAsset[];
}

export const getReviewQueue = async (): Promise<ReviewItem[]> => {
  const { data } = await api.get<{ queue: ReviewItem[] }>(
    '/admin/review-queue'
  );
  return Array.isArray(data?.queue) ? data.queue : [];
};

/**
 * Record a decision about a package.
 *
 * `approve` publishes its assets; `decline` keeps them hidden, takes the
 * package out of the queue and gives its owner a reason. ⚠️ Declining is NOT
 * the same as leaving it pending — that difference is the whole reason the
 * decision stopped being a boolean.
 */
export const decidePackage = async (
  pkg: string,
  action: 'approve' | 'decline',
  reason = ''
): Promise<void> => {
  await api.patch(`/admin/packages/${encodeURIComponent(pkg)}`, {
    action,
    reason,
  });
};
